/**
 * Vouchers (event payments): an organizer records a cash, check or Zelle payment as a code the guest redeems at checkout.
 * Pure helpers only. The code is never stored in the browser; the server keeps a hash of it.
 */
export type VoucherMethod = 'cash' | 'check' | 'zelle';
export const VOUCHER_METHODS: ReadonlyArray<{ value: VoucherMethod; label: string }> = [
  { value: 'cash', label: 'Cash' }, { value: 'check', label: 'Check' }, { value: 'zelle', label: 'Zelle' },
];
export const BAD_VOUCHER = "That voucher isn't valid.";

/** The code as the server compares it: hex digits only, upper case (dashes and spaces a guest typed are dropped). */
export const normaliseCode = (s: string): string => s.replace(/[^0-9a-f]/gi, '').toUpperCase();

/** The code shown once to the organizer: `XXXX-XXXX-XXXX-XXXX`. */
export const formatCode = (s: string): string => (normaliseCode(s).match(/.{1,4}/g) ?? []).join('-');

/** What the voucher pays of this charge: its balance, never more than the charge, never below zero. */
export const coverOf = (remaining: number, charge: number): number => Math.max(0, Math.min(remaining, charge));

/** The invoice line of a voucher payment, worked out from the method and the number on record. */
export function voucherLine(method: string | null | undefined, reference: string | null | undefined): string {
  const ref = (reference ?? '').trim();
  const num = ref ? ` #${ref}` : '';
  if (method === 'check') return `Paid by check${num} — voucher`;
  if (method === 'zelle') return `Paid by Zelle${num} — voucher`;
  return `Paid by cash${num} — voucher`;
}

/** The reference rules the database holds (1–40 characters, no run of 9 digits, required unless cash). `null` = fine. */
export function referenceProblem(method: VoucherMethod, reference: string): string | null {
  const r = reference.trim();
  if (!r) return method === 'cash' ? null : 'Enter the check number or Zelle confirmation.';
  if (r.length > 40) return 'Use 40 characters or fewer.';
  if (/[0-9]{9,}/.test(r)) return 'That looks like an account number. Enter the check number or confirmation only.';
  return null;
}
